import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import config from "../../config";
import terms from "../../config/terminology";
import { formatCurrency } from "../../utils/formatCurrency";
import "./overlay.css";

export default function RecentTipsOverlay() {
  const { username } = useParams();
  const [recentDonations, setRecentDonations] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get user profile
        const profileRes = await fetch(`${config.API_URL}/users/username/${username}/profile`);
        if (profileRes.ok) {
          const profile = await profileRes.json();
          setUserProfile(profile);

          // Fetch recent donations for this user
          const donationsRes = await fetch(`${config.API_URL}/donations/recent?creator_id=${profile.id}&limit=10&completed_only=true`);
          if (donationsRes.ok) {
            const donations = await donationsRes.json();
            setRecentDonations(donations);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [username]);

  // Poll for new donations at configurable interval
  useEffect(() => {
    if (!userProfile?.id) return;

    let intervalId = null;

    const fetchDonations = async () => {
      try {
        const donationsRes = await fetch(`${config.API_URL}/donations/recent?creator_id=${userProfile.id}&limit=10&completed_only=true`);
        if (donationsRes.ok) {
          const donations = await donationsRes.json();
          setRecentDonations(donations);
        }
      } catch (error) {
        console.log("Could not fetch recent donations:", error);
      }
    };

    const setupPolling = async () => {
      try {
        const res = await fetch(`${config.API_URL}/settings/donation-polling-interval`);
        if (res.ok) {
          const { polling_interval } = await res.json();
          if (polling_interval > 0) {
            intervalId = setInterval(fetchDonations, polling_interval * 1000);
          }
        }
      } catch (error) {
        console.log("Could not fetch polling interval, using default:", error);
        // Default to 10 seconds if we can't fetch the setting
        intervalId = setInterval(fetchDonations, 10000);
      }
    };

    setupPolling();

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [userProfile?.id]);

  if (loading) {
    return (
      <div className="overlay-loading">
        <p className="overlay-loading-text">Loading...</p>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="overlay-loading">
        <p style={{ color: "#EF4444", fontSize: "1rem" }}>Creator not found</p>
      </div>
    );
  }

  return (
    <div className="overlay-container">
      {recentDonations.length > 0 ? (
        <div className="tips-panel">
          <h3 className="tips-header">
            Recent {terms.Donations}
          </h3>
          <div>
            {recentDonations.slice(0, 5).map((donation, index) => (
              <div key={donation.id} className="tip-item">
                <div className="tip-header-row">
                  <span className="tip-donor-name">
                    {donation.name || "Anonymous"}
                  </span>
                  <span className="tip-amount">
                    {formatCurrency(donation.amount)}
                  </span>
                </div>
                {donation.message && (
                  <p className="tip-message">
                    "{donation.message}"
                  </p>
                )}
                <p className="tip-timestamp">
                  {new Date(donation.created_at).toLocaleDateString()} at {new Date(donation.created_at).toLocaleTimeString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="tips-empty">
          <p className="tips-empty-text">
            No recent {terms.donations.toLowerCase()} yet
          </p>
        </div>
      )}
    </div>
  );
}